import HistoryIcon from '@mui/icons-material/History'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material'
import { Suspense, useState } from 'react'
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar'
import 'react-circular-progressbar/dist/styles.css'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import QueryErrorBoundary from '../components/QueryErrorBoundary'
import BiReports from '../products/Common/BiReportsComponent'
import { useAppSelector } from '../store/store.hooks'
import { QcTestDefinitionListItem } from '../types'
import AdminToolsPanel from './components/AdminToolsPanel'
import { useQualityTestList } from './services/QualityControlHooks'

const scoreColor = (s: number) => {
  if (s >= 80) return '#4caf50'
  if (s >= 50) return '#ff9800'
  return '#f44336'
}

const ScoreDonut = ({ score }: { score: number | null }) => {
  const { t } = useTranslation()
  const size = 72
  const s = score === null ? 0 : Math.max(0, Math.min(100, score))
  const color = score === null ? '#e0e0e0' : scoreColor(s)

  return (
    <Tooltip title={t('qualityControl.averageScoreTooltip')}>
      <Box sx={{ width: size, height: size, flexShrink: 0 }}>
        <CircularProgressbar
          value={s}
          text={score === null ? '—' : `${Math.round(s)}%`}
          strokeWidth={12}
          styles={buildStyles({
            pathColor: color,
            trailColor: '#e0e0e0',
            textColor: color,
            textSize: score === null ? '28px' : '24px',
            strokeLinecap: 'butt',
          })}
        />
      </Box>
    </Tooltip>
  )
}

const QualityTestList = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { qualityTests } = useQualityTestList()
  const isAdmin = useAppSelector((s) => s.auth.userDetails?.isAdmin ?? false)
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null)

  if (qualityTests.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">{t('qualityControl.noTests')}</Alert>
        {isAdmin && (
          <AdminToolsPanel
            onRecomputeSuccess={(count) => setSnackbarMessage(t('qualityControl.recomputeSuccess', { count }))}
            onRecomputeError={() => setSnackbarMessage(t('qualityControl.recomputeError'))}
          />
        )}
      </Box>
    )
  }

  // Group tests by category
  const groupedTests = qualityTests.reduce(
    (acc, test) => {
      const category: string = test.category || t('qualityControl.uncategorized')
      if (!acc[category]) {
        acc[category] = []
      }
      acc[category].push(test)
      return acc
    },
    {} as Record<string, QcTestDefinitionListItem[]>,
  )

  const handleTestClick = (testId: string) => {
    navigate(`/quality-control/${testId}/execute`)
  }

  const handleHistoryClick = (testId: string) => {
    navigate(`/quality-control/${testId}/history`)
  }

  return (
    <Box sx={{ px: 3, pt: 1, pb: 3 }}>
      <Snackbar
        open={snackbarMessage !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
      />
      {Object.entries(groupedTests).map(([category, tests]) => (
        <Box key={category} sx={{ mb: 4 }}>
          {/* Category header row: uppercase label + count chip */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, mt: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
              {category}
            </Typography>
            <Chip
              label={`${tests.length}`}
              size="small"
              sx={{ bgcolor: 'secondary.main', color: 'secondary.contrastText', fontWeight: 700 }}
            />
          </Box>

          <Grid container spacing={2}>
            {tests.map((test) => (
              <Grid
                key={test.id}
                size={{
                  xs: 12, // 1 card per row on extra-small screens
                  sm: 6, // 2 cards per row on small screens
                  md: 4, // 3 cards per row on medium screens
                  lg: 3, // 4 cards per row on large screens
                }}
              >
                <Card
                  elevation={0}
                  sx={{
                    transition: 'all 0.2s',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    overflow: 'hidden',
                    ...(!isAdmin && { borderLeft: '4px solid', borderColor: 'primary.main' }),
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  {/* Vertical yellow edit strip — admin only */}
                  {isAdmin && (
                    <Box
                      sx={{
                        width: 28,
                        bgcolor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        cursor: 'pointer',
                        '&:hover': { filter: 'brightness(0.9)' },
                      }}
                      onClick={() => navigate(`/quality-control/${test.testGroupId}/edit`)}
                    >
                      <Typography
                        sx={{
                          transform: 'rotate(-90deg)',
                          color: 'primary.contrastText',
                          fontWeight: 700,
                          fontSize: '0.6rem',
                          letterSpacing: 2,
                          textTransform: 'uppercase',
                          whiteSpace: 'nowrap',
                          userSelect: 'none',
                        }}
                      >
                        Edit
                      </Typography>
                    </Box>
                  )}

                  <CardContent
                    sx={{
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      p: 1.5,
                      '&:last-child': { pb: 1.5 },
                    }}
                  >
                    {/* Top row: info on the left, donut on the right */}
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5, alignItems: 'flex-start' }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.25 }}>
                          {test.displayName}
                        </Typography>

                        {/* Description — max 2 lines */}
                        {test.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mb: 0.5,
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                            }}
                          >
                            {test.description}
                          </Typography>
                        )}

                        {/* Execution count chip */}
                        {test.metrics && (
                          <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`${test.metrics.executionCount} ${t('qualityControl.executionCount').toLowerCase()}`}
                            />
                          </Box>
                        )}
                      </Box>

                      {/* Score donut */}
                      <Box sx={{ flexShrink: 0, overflow: 'hidden', position: 'relative' }}>
                        <ScoreDonut score={test.metrics?.averageScore ?? null} />
                        {(test.metrics?.deletedExecutionCount ?? 0) > 0 && (
                          <Tooltip
                            title={t('qualityControl.deletedExecutionsInRange', {
                              count: test.metrics!.deletedExecutionCount,
                            })}
                          >
                            <WarningAmberIcon
                              fontSize="small"
                              color="warning"
                              sx={{ position: 'absolute', top: 0, right: 0, fontSize: '0.9rem' }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </Box>

                    {/* CTA buttons — full width, stacked */}
                    <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => handleTestClick(test.id)}
                        fullWidth
                      >
                        {t('qualityControl.executeTest')}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<HistoryIcon />}
                        onClick={() => handleHistoryClick(test.id)}
                        fullWidth
                      >
                        {t('qualityControl.viewHistory')}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
      {isAdmin && (
        <AdminToolsPanel
          onRecomputeSuccess={(count) => setSnackbarMessage(t('qualityControl.recomputeSuccess', { count }))}
          onRecomputeError={() => setSnackbarMessage(t('qualityControl.recomputeError'))}
        />
      )}
      <Box sx={{ height: 600, mt: 4 }}>
        <QueryErrorBoundary>
          <Suspense
            fallback={
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            }
          >
            <BiReports nameList={['QcReport']} />
          </Suspense>
        </QueryErrorBoundary>
      </Box>
    </Box>
  )
}

export default QualityTestList
